import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { getApp } from 'firebase/app';
import {
  applyActionCode,
  Auth,
  checkActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAuth,
  getMultiFactorResolver,
  getRedirectResult,
  GoogleAuthProvider,
  IdTokenResult,
  linkWithCredential,
  linkWithPopup,
  multiFactor,
  MultiFactorResolver,
  OAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  TotpMultiFactorGenerator,
  TotpSecret,
  unlink,
  updatePassword,
  User,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { AuthError } from '@auth/auth.enum';
import { environment } from '@env/environment';
import { Navigate } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { Commands } from '@shared/enums/commands.enum';
import { InviteData, SignupData } from '@shared/models/account.model';
import { UserInfo } from '@shared/models/auth.model';
import { mapListKeys } from '@shared/operators/map-list-keys.operator';
import { mapObjectKey } from '@shared/operators/map-object-key.operator';
import { shareRef } from '@shared/operators/share-ref.operator';
import { AuditLogService } from '@shared/services/audit-log.service';
import { CloudFunctions } from '@shared/services/cloud-functions.service';
import { DatabaseWrapper } from '@shared/services/database-wrapper.service';
import { ZefApi } from '@shared/services/zef-api.service';
import { AuthDoneFetchAccountData } from '@shared/states/account.actions';
import {
  AuthenticationError,
  MonitorLoginExpiration,
  MonitorMFAReset,
  ResetUserInfo,
  SaveClaimsOnAuthTokenChange,
  SaveTokenOnAuthTokenChange,
  SignOutWithRedirect,
  UpdateUserOnAuthTokenChange,
} from '@shared/states/auth.actions';
import { AuthState } from '@shared/states/auth.state';
import { ServerError } from '@shared/states/error.actions';
import { RouterState } from '@shared/states/router.state';
import { LocalStorage } from 'ngx-webstorage';
import {
  BehaviorSubject,
  combineLatest,
  concat,
  firstValueFrom,
  from,
  Observable,
  of,
  ReplaySubject,
  Subject,
  throwError,
  timer,
} from 'rxjs';
import { catchError, map, switchMap, take, tap, toArray } from 'rxjs/operators';
import { DialogControl } from './dialog-control.service';

type AuthCustomResponse = {
  token?: string;
  // Only used for private link
  share?: { surveyKey: string; reportKey: string; teamKey: string };
};

@Injectable({
  providedIn: 'root',
})
export class AuthManager {
  private db = inject(DatabaseWrapper);
  private injector = inject(Injector);
  private za = inject(ZefApi);
  private store = inject(Store);
  private al = inject(AuditLogService);
  private cf = inject(CloudFunctions);
  private dc = inject(DialogControl);

  @LocalStorage('consent')
  trackingAllowed: boolean | undefined;
  @LocalStorage('mfaFactorId') mfaFactorId: 'totp' | 'phone' | undefined;
  signup$: BehaviorSubject<SignupData | null> = new BehaviorSubject(null);

  // TODO: Remove this and update Analytics to use different data
  public readonly onSignupComplete = new Subject<User>();

  private user$?: ReplaySubject<User> = new ReplaySubject<User>(1);

  private readonly ssoLoginErrorMessage = $localize`Single sign-on could not be completed. Please try again or contact your organization's administrator.`;

  private totpSecret: TotpSecret | null = null;
  private recaptchaVerifier: RecaptchaVerifier | null = null;

  private userInfo(user: User): UserInfo {
    if (!user) {
      return null;
    }

    const enrolledFactors = multiFactor(user).enrolledFactors;
    const defaultMfaFactor = this.mfaFactorId || enrolledFactors?.[0]?.factorId;

    return {
      $key: user.uid,
      uid: user.uid,
      email: user.email,
      photoURL: user.photoURL,
      phoneNumber: user.phoneNumber,
      displayName: user.displayName,
      providerData: user.providerData,
      enrolledFactors,
      defaultMfaFactor,
      emailVerified: user.emailVerified,
      creationTime: new Date(user.metadata.creationTime).getTime(),
      lastSignInTime: new Date(user.metadata.lastSignInTime).getTime(),
    };
  }

  currentUser(): Observable<User> {
    return this.user$.pipe(take(1));
  }

  currentAuth(): Auth {
    return runInInjectionContext(this.injector, () => getAuth(getApp(environment.firebase.appId)));
  }

  // Init authentication for the main service and listen for auth changes.
  init() {
    const auth = this.currentAuth();

    auth.onIdTokenChanged((user: User) => this.user$.next(user));

    const init$ = this.user$.pipe(
      switchMap((user) => user?.getIdTokenResult().then((authToken) => [user, authToken]) || of([null, null])),
      switchMap(([authUser, authToken]: [User, IdTokenResult]) => {
        const userInfo = this.userInfo(authUser);
        const emailDomain = (authUser?.email || '').split('@')[1];

        if (userInfo?.enrolledFactors?.length > 0) {
          this.store.dispatch(new MonitorMFAReset(userInfo.uid));
        }

        const authLocked = this.store.selectSnapshot(AuthState.locked);

        if (authLocked) {
          console.log('Skipping auth initialization');

          return of(void 0);
        }

        const isSSOLogin = authToken?.claims?.firebase?.sign_in_provider?.startsWith('oidc.');

        console.log('Auth initialization', userInfo, authLocked, isSSOLogin, emailDomain);

        return (isSSOLogin || !emailDomain ? of(false) : this.checkSSODomain(emailDomain)).pipe(
          switchMap((ssoLoginRequired) => {
            const is2faRoute = (location?.pathname || '').includes('/2fa');
            const isLoginRoute = (location?.pathname || '').includes('/login');
            const isLogoutRoute = (location?.pathname || '').includes('/logout');
            const isInviteRoute = (location?.pathname || '').includes('/invite');
            const isSignupRoute = (location?.pathname || '').includes('/signup');
            const isVerifyRoute = (location?.pathname || '').includes('/verify');
            const isPaymentRoute = (location?.pathname || '').includes('/payment');
            const isSSOLoginRoute = (location?.pathname || '').includes('/login/');

            console.log('Handling authentication:', authUser != null, {
              is2faRoute,
              isLoginRoute,
              isLogoutRoute,
              isInviteRoute,
              isSignupRoute,
              isVerifyRoute,
              isPaymentRoute,
              isSSOLoginRoute,
              ssoLoginRequired,
            });

            if (authUser != null && !isLogoutRoute) {
              this.auth(authUser, authToken);

              const queryParams = this.store.selectSnapshot(RouterState.queryParams);

              if (ssoLoginRequired) {
                this.store.dispatch(new SignOutWithRedirect(true, 'AuthManager.init() - SSO login needed'));
              } else if (!isSSOLoginRoute) {
                if (isSSOLogin) {
                  this.store.dispatch(new MonitorLoginExpiration(authUser.uid));
                }

                if (isLoginRoute) {
                  this.store.dispatch([
                    new AuthDoneFetchAccountData(userInfo?.$key),
                    new Navigate([`/surveys`], queryParams),
                  ]);

                  this.al.logAuthAccess(of(authUser?.email), 'LOGIN');
                } else {
                  this.store.dispatch(new AuthDoneFetchAccountData(userInfo?.$key));
                }
              }

              return of(void 0);
            } else if (
              !authUser &&
              !is2faRoute &&
              !isLoginRoute &&
              !isInviteRoute &&
              !isSignupRoute &&
              !isVerifyRoute &&
              !isPaymentRoute
            ) {
              this.store.dispatch(new Navigate([`/login`]));
              return of(void 0);
            } else {
              return of(void 0);
            }
          }),
        );
      }),
      shareRef(),
    );

    init$.subscribe();

    return init$.pipe(take(1));
  }

  // Authenticate the user and store state information into the auth store.
  auth(user: User, authToken: IdTokenResult) {
    const userInfo: UserInfo = this.userInfo(user);

    this.store.dispatch([
      new UpdateUserOnAuthTokenChange(userInfo),
      new SaveTokenOnAuthTokenChange(authToken.token),
      new SaveClaimsOnAuthTokenChange(authToken.claims),
    ]);
  }

  signOut() {
    const email = this.currentAuth().currentUser?.email;

    return concat(
      ...(email ? [this.al.sendAuthLog({ status: 'SUCCESS', auth: 'LOGOUT', email })] : []),
      this.store.dispatch(new ResetUserInfo()),
      this.runWithAuth((auth) => auth.signOut()),
    ).pipe(toArray());
  }

  deleteUser() {
    return this.runWithUser((user) => user?.delete());
  }

  refreshToken() {
    return this.runWithUser((user) => user.getIdToken(true)).pipe(
      tap((token) => this.store.dispatch(new SaveTokenOnAuthTokenChange(token))),
    );
  }

  signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'consent' });

    return this.runWithDefAuth((auth) => signInWithPopup(auth, provider));
  }

  signInWithMicrosoft() {
    const provider = new OAuthProvider('microsoft.com');
    // provider.setCustomParameters({ prompt: 'consent' });

    return this.runWithDefAuth((auth) => signInWithPopup(auth, provider));
  }

  unlinkPassword() {
    return this.runWithUser((user) => unlink(user, 'password')).pipe(
      map((user) => this.userInfo(user)),
      tap((info) => this.store.dispatch(new UpdateUserOnAuthTokenChange(info))),
    );
  }

  resetPassword(code: string, password: string) {
    return this.runWithAuth((auth) => confirmPasswordReset(auth, code, password));
  }

  linkPassword(email: string, password: string) {
    const credential = EmailAuthProvider.credential(email, password);

    return this.runWithUser((user) => linkWithCredential(user, credential)).pipe(
      map((cred) => this.userInfo(cred?.user)),
      tap((info) => this.store.dispatch(new UpdateUserOnAuthTokenChange(info))),
    );
  }

  createUserWithPassword(email: string, password: string) {
    return this.runWithAuth((auth) => createUserWithEmailAndPassword(auth, email, password)).pipe(
      map((cred) => this.userInfo(cred?.user)),
      tap((info) => this.store.dispatch(new UpdateUserOnAuthTokenChange(info))),
    );
  }

  unlinkProvider(provider: string) {
    return this.runWithUser((user) => unlink(user, provider)).pipe(
      map((user) => this.userInfo(user)),
      tap((info) => this.store.dispatch(new UpdateUserOnAuthTokenChange(info))),
    );
  }

  linkGoogle(login_hint?: string) {
    const provider = new GoogleAuthProvider();

    if (login_hint) {
      provider.setCustomParameters({ login_hint, prompt: 'consent' });
    }

    return this.runWithUser((user) => linkWithPopup(user, provider)).pipe(
      map((cred) => this.userInfo(cred?.user)),
      tap((info) => this.store.dispatch(new UpdateUserOnAuthTokenChange(info))),
    );
  }

  linkMicrosoft(login_hint?: string) {
    const provider = new OAuthProvider('microsoft.com');

    if (login_hint) {
      provider.setCustomParameters({ login_hint /* prompt: 'consent' */ });
    }

    return this.runWithUser((user) => linkWithPopup(user, provider)).pipe(
      map((cred) => this.userInfo(cred?.user)),
      tap((info) => this.store.dispatch(new UpdateUserOnAuthTokenChange(info))),
    );
  }

  /*
   * SSO authentication is performed in 3 steps, login states for the steps are:
   * 1. Redirected:    The user is redirected to the SSO login url
   * 2. "Finished":    Teams update done and user is redirected to the surveys page
   */
  ssoLogin(ssoDomain: string) {
    let provider;

    this.getSSOSettings(ssoDomain).subscribe((settings) => {
      if (!settings) {
        this.store.dispatch(new SignOutWithRedirect(true, 'AuthManager.ssoLogin() - No SSO settings'));
      } else {
        console.log('Performing oidc login');

        const queryParams = this.store.selectSnapshot(RouterState.queryParams);

        provider = new OAuthProvider('oidc.' + (settings.providerId || 'sso'));

        provider.setCustomParameters({
          tenant: settings.tenant || ssoDomain,
          product: queryParams.zefApp || 'zeffi',
          organization: queryParams.zefOrg || '',
        });

        this.store.dispatch(new ResetUserInfo());

        window.history.replaceState({}, '', '/login');

        const isPopupRequired =
          window.location.hostname === 'localhost' || window.location.hostname.endsWith('web.app');

        const signIn = isPopupRequired ? signInWithPopup : signInWithRedirect;

        this.runWithSSOAuth(settings.tenant, (auth) => auth.signOut().then(() => signIn(auth, provider)))
          .pipe(
            tap(() => {
              if (isPopupRequired) {
                this.store.dispatch(new Navigate(['/']));
              }
            }),
            catchError((error) => {
              console.log('SSO login failed', error);

              sessionStorage.setItem('login-error', this.ssoLoginErrorMessage);

              this.store.dispatch(new SignOutWithRedirect(true, 'AuthManager.ssoLogin() - SSO login failed'));

              return of(null);
            }),
          )
          .subscribe();
      }
    });
  }

  ssoRedirectError(): Observable<string | null> {
    return this.runWithAuth((auth) => getRedirectResult(auth)).pipe(
      map(() => null),
      catchError((error) => {
        console.log('SSO login failed', error);

        return of(this.ssoLoginErrorMessage);
      }),
    );
  }

  /**
   * General account management helper functions.
   */

  public updatePassword(password: string): Observable<boolean> {
    return this.runWithUser((user) => updatePassword(user, password)).pipe(
      map(() => true),
      catchError((error) => {
        console.log('Password update failed', error);

        return throwError(null);
      }),
    );
  }

  public signInWithToken(token: string): Observable<User | null> {
    return this.runWithAuth((auth) => signInWithCustomToken(auth, token)).pipe(
      map((cred) => cred?.user || null),
      catchError((error) => {
        console.log('Failed to sign in with custom token', error);

        return throwError(null);
      }),
    );
  }

  public reAuthenticateWithGoogle(): Observable<boolean> {
    const provider = new GoogleAuthProvider();

    return this.runWithUser((user) => reauthenticateWithPopup(user, provider)).pipe(map(() => true));
  }

  public signInWithEmailPassword(email: string, password: string): Observable<User | null> {
    return this.runWithAuth((auth) => signInWithEmailAndPassword(auth, email, password)).pipe(
      map((cred) => cred?.user || null),
      catchError((error) => {
        console.log('Email sign-in failed', error);

        throw error;
      }),
    );
  }

  public reAuthenticateWithEmailPassword(email: string, password: string): Observable<User | null> {
    const credential = EmailAuthProvider.credential(email, password);

    return this.runWithUser((user) => reauthenticateWithCredential(user, credential)).pipe(
      map((cred) => cred?.user || null),
      catchError((error) => {
        console.log('Email sign-in failed', error);

        return throwError(error);
      }),
    );
  }

  public sendEmailPasswordResetEmail(email: string): Observable<boolean> {
    return this.runWithAuth((auth) => sendPasswordResetEmail(auth, email)).pipe(
      map(() => true),
      catchError((error) => {
        console.log('Email sending failed', error);

        return throwError(error);
      }),
    );
  }

  public verifyEmailPasswordResetCode(actionCode: any): Observable<string> {
    return this.runWithAuth((auth) => verifyPasswordResetCode(auth, actionCode)).pipe(
      catchError((error) => {
        console.log('Code verification failed', error);

        return throwError(error);
      }),
    );
  }

  public verifyEmailPasswordEmailAddress(actionCode: any): Observable<boolean> {
    return this.runWithAuth((auth) => applyActionCode(auth, actionCode)).pipe(
      map(() => true),
      catchError((error) => {
        console.log('Code verification failed', error);

        return throwError(error);
      }),
    );
  }

  public confirmEmailPasswordEmailRecovery(actionCode: any): Observable<string> {
    return this.runWithAuth((auth) =>
      checkActionCode(auth, actionCode).then((info) => applyActionCode(auth, actionCode).then(() => info?.data?.email)),
    ).pipe(
      catchError((error) => {
        console.log('Code confirmation failed', error);

        return throwError(error);
      }),
    );
  }

  public confirmEmailPasswordResetPassword(actionCode: any, newPassword: string): Observable<void> {
    return this.runWithAuth((auth) => confirmPasswordReset(auth, actionCode, newPassword)).pipe(
      catchError((error) => {
        console.log('Reset confirmation failed', error);

        return throwError(error);
      }),
    );
  }

  private runOutside<T>(retriever: () => Promise<T>): Observable<T> {
    return from(retriever());
  }

  private runWithUser<T>(run: (user: User) => Promise<T>): Observable<T> {
    return this.currentUser().pipe(switchMap((user) => this.runOutside<T>(() => run(user))));
  }

  private runWithAuth<T>(run: (auth: Auth) => Promise<T>): Observable<T> {
    const auth = this.currentAuth();

    return runInInjectionContext(this.injector, () => this.runOutside<T>(() => run(auth)));
  }

  private runWithDefAuth<T>(run: (auth: Auth) => Promise<T>): Observable<T> {
    const auth = this.currentAuth();

    auth.tenantId = null;

    return runInInjectionContext(this.injector, () => this.runOutside<T>(() => run(auth)));
  }

  private runWithSSOAuth<T>(tenantId: string | undefined, run: (auth: Auth) => Promise<T>): Observable<T> {
    const auth = this.currentAuth();

    auth.tenantId = tenantId || null;

    return runInInjectionContext(this.injector, () => this.runOutside<T>(() => run(auth)));
  }

  public sendInvite(verify?: boolean, redirect?: string, locale?: string): Observable<any> {
    return this.za
      .post(
        'invite/signup',
        {},
        {
          type: verify ? 'verify' : 'login',
          locale: locale || 'en',
          redirect: redirect || null,
        },
      )
      .pipe(
        catchError((error: any) => {
          this.store.dispatch(new ServerError(error));

          return of(error);
        }),
      );
  }

  public checkInvites(email: string): Observable<InviteData[]> {
    return this.db
      .list<InviteData>(`/invites`, (ref) => ref.orderByChild('email').equalTo(email))
      .snapshotChanges()
      .pipe(take(1), mapListKeys());
  }

  public getInvite(inviteKey: string): Observable<InviteData> {
    const teamInvite$ = this.db.object<InviteData>(`/invites/${inviteKey}`).snapshotChanges().pipe(mapObjectKey());

    const funnelInvite$ = this.db
      .object<InviteData>(`/funnelinvites/${inviteKey}`)
      .snapshotChanges()
      .pipe(
        mapObjectKey(),
        switchMap((inviteData: InviteData) => {
          if (!inviteData) {
            return of(null);
          } else {
            return of(inviteData);
          }
        }),
      );

    return combineLatest([teamInvite$, funnelInvite$]).pipe(
      map(([teamInvite, funnelInvite]) => teamInvite || funnelInvite),
    );
  }

  verifyCustomLogin(verifyKey: string): Observable<string> {
    return this.za.post<AuthCustomResponse>('auth/custom', { verifyKey }).pipe(
      switchMap((response: AuthCustomResponse) =>
        !response?.token
          ? of(null)
          : this.store.dispatch(new ResetUserInfo()).pipe(switchMap(() => this.signInWithToken(response.token))),
      ),
      map((user) => user?.uid),
    );
  }

  verifyScaKey(scaKey: string) {
    return this.za.post<AuthCustomResponse>('auth/custom', { scaKey }).pipe(
      switchMap((response: AuthCustomResponse) =>
        !response?.token
          ? of(null)
          : this.store.dispatch(new ResetUserInfo()).pipe(switchMap(() => this.signInWithToken(response.token))),
      ),
      map((user) => Boolean(user)),
    );
  }

  verifyPrivateLink(linkKey: string, email?: string) {
    return this.za
      .post<AuthCustomResponse>('auth/custom', { linkKey, email })
      .pipe(map((response) => ({ ...response?.share, token: response.token })));
  }

  loginExpires(userUid: string) {
    return timer(60000).pipe(
      switchMap(() => this.db.object<any>(`/auth/login/${userUid}/loginExpires`).snapshotChanges()),
      catchError(() => of(null)),
    );
  }

  mfaResetTime(userUid: string) {
    return this.db
      .object<any>(`/auth/login/${userUid}/mfaReset`)
      .snapshotChanges()
      .pipe(
        map((snapshot) => snapshot.payload.val()),
        catchError(() => of(null)),
      );
  }

  mfaReset(userUid: string) {
    return from(this.db.object<any>(`/auth/login/${userUid}/mfaReset`).remove());
  }

  getPollToken(surveyKey: string, teamKey: string, linkKey: string, sessionKey: string) {
    return this.za.post<any>('auth/livepoll', { surveyKey, teamKey, linkKey, sessionKey });
  }

  getSSOSettings(ssoDomain: string) {
    return ssoDomain
      ? this.db.object<any>(`/sso/${ssoDomain}/loginSettings`).snapshotChanges().pipe(mapObjectKey())
      : of(null);
  }

  checkSSODomain(emailDomain: string) {
    return !emailDomain
      ? of(null)
      : this.db
          .object<any>(`/domains/${emailDomain.replace(/\./g, ',')}`)
          .valueChanges()
          .pipe(
            take(1),
            map((value) => !!value),
          );
  }

  getSignupData(teamHandle: string): Observable<SignupData | null> {
    if (teamHandle) {
      return this.db
        .list<SignupData>(`/signups`, (ref) => ref.orderByChild(`handle`).equalTo(teamHandle))
        .snapshotChanges()
        .pipe(
          take(1),
          mapListKeys('parentTeam'),
          map((data) => data[0] || null),
          tap((data) => this.signup$.next(data)),
        );
    } else {
      const userKey = this.store.selectSnapshot(AuthState.userUid);

      return this.userSignupData(userKey);
    }
  }

  clearSignupData() {
    const userKey = this.store.selectSnapshot(AuthState.userUid);
    this.db.object<SignupData | boolean>(`/auth/signup/${userKey}`).remove();
    this.signup$.next(null);
  }

  userSignupData(userKey: string) {
    return this.db
      .object<SignupData>(`/auth/signup/${userKey}`)
      .valueChanges()
      .pipe(
        take(1),
        tap((data) => this.signup$.next(data)),
      );
  }

  handleAuthError(error: any) {
    // console.warn('AuthManager.handleAuthError: ', error.code, error);

    const requresReloadAuthError = [
      'auth/invalid-verification-code',
      'auth/invalid-app-credential',
      'auth/invalid-phone-number',
    ].includes(error.code);

    const passwordAuthError = [
      'auth/credential-already-in-use',
      'auth/email-already-in-use',
      'auth/provider-already-linked',
    ].includes(error.code);

    if (error.code === 'auth/multi-factor-auth-required') {
      const resolver = getMultiFactorResolver(this.currentAuth(), error);
      return of({ code: error.code, resolver });
    } else if (requresReloadAuthError) {
      this.store.dispatch(new AuthenticationError(AuthError.RELOAD_REQUIRED));
      return of({ code: error.code });
    } else if (passwordAuthError) {
      return of({ code: error.code });
    } else if (error.code === 'auth/user-token-expired') {
      this.store
        .dispatch(new SignOutWithRedirect(true, 'AuthManager.handleAuthError() - Token expired'))
        .pipe(tap(() => window.location.reload()));

      return of({ code: error.code });
    } else if (error.code === 'auth/too-many-requests') {
      this.store.dispatch(new AuthenticationError(AuthError.TOO_MANY_REQUESTS));
      return of({ code: error.code });
    } else if (error.code === 'auth/requires-recent-login') {
      const dialogTitle = $localize`Recent Login Required`;
      const dialogContent = $localize`Please log in again to continue.`;
      this.dc
        .openErrorDialog(dialogTitle, dialogContent, ['OK'])
        .afterClosed()
        .subscribe((action) => {
          if (action) {
            this.store.dispatch(new SignOutWithRedirect(true, 'AuthManager.handleAuthError() - Requires recent login'));
          }
        });

      return of({ code: error.code });
    } else if (error.code === 'auth/internal-error' && error.message.includes('PERMISSION_DENIED')) {
      return of({ code: 'auth/signup-blocked' });
    } else if (error.code === 'auth/internal-error' && error.message.includes('BLOCKING_FUNCTION_ERROR')) {
      this.store.dispatch(new AuthenticationError(AuthError.RELOAD_REQUIRED));
      return of(null);
    } else {
      console.warn('AuthManager.handleAuthError: Unhandled error', error.code, error);
      return of(null);
    }
  }

  verifyMfaSignIn(resolver: MultiFactorResolver, factorId: string, verificationCode: string) {
    const authMfa: any = this.store.selectSnapshot(AuthState.mfa);
    const hint = resolver.hints.find((h) => h.factorId === factorId);
    if (factorId === 'totp') {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, verificationCode);
      return from(resolver.resolveSignIn(assertion)).pipe(catchError((error) => this.handleAuthError(error)));
    } else if (factorId === 'phone') {
      const cred = PhoneAuthProvider.credential(authMfa?.verificationId, verificationCode);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      return from(resolver.resolveSignIn(assertion)).pipe(catchError((error) => this.handleAuthError(error)));
    }
  }

  generateTOTPSecret(appName: string) {
    // https://firebase.google.com/docs/auth/web/totp-mfa
    return this.currentUser().pipe(
      switchMap(async (currentUser) => {
        const multiFactorSession = await multiFactor(currentUser).getSession();
        this.totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);

        const secretKey = this.totpSecret.secretKey;

        if (this.totpSecret) {
          const totpUri = this.totpSecret.generateQrCodeUrl(currentUser.email, appName);
          const cfObservable = this.cf.post(Commands.MfaAuth, 'mfaAuth/qrCode', { uri: totpUri });
          const qrCode = await firstValueFrom(cfObservable);
          return { qrCode, secretKey };
        }
      }),
    );
  }

  verifyTOTPCode(verificationCode: string) {
    const authMfa = this.store.selectSnapshot(AuthState.mfa);
    return this.currentUser().pipe(
      switchMap(async (currentUser) => {
        const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(this.totpSecret, verificationCode);
        await multiFactor(currentUser).enroll(multiFactorAssertion, 'ZEF 2FA');
        return null;
      }),
      catchError((error) => {
        return of({ ...authMfa, error: error.code });
      }),
    );
  }

  verifyPhoneCode(verificationCode: string) {
    const authMfa: any = this.store.selectSnapshot(AuthState.mfa);
    return this.currentUser().pipe(
      switchMap(async (currentUser) => {
        const cred = PhoneAuthProvider.credential(authMfa.verificationId, verificationCode);
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
        await multiFactor(currentUser).enroll(multiFactorAssertion, 'Phone number');
        return null;
      }),
      catchError((error) => {
        return of({ ...authMfa, error: error.code });
      }),
    );
  }

  private resetRecaptcha() {
    this.recaptchaVerifier?.clear();
    this.recaptchaVerifier = null;

    const container = document.getElementById('recaptcha-container-id');
    if (container) {
      const parent = container.parentElement;
      if (parent) {
        const newContainer = document.createElement('div');
        newContainer.id = 'recaptcha-container-id';
        parent.replaceChild(newContainer, container);
      } else {
        container.innerHTML = '';
      }
    }
  }

  generateSMSSecret(phoneHint: any) {
    // https://firebase.google.com/docs/auth/web/multi-factor
    return this.currentUser().pipe(
      switchMap(async (currentUser) => {
        const auth = this.currentAuth();
        const phoneAuthProvider = new PhoneAuthProvider(auth);

        try {
          this.resetRecaptcha();
          let verificationId = null;
          this.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container-id', {
            size: 'invisible',
          });

          const phoneInfoOptions =
            typeof phoneHint === 'string'
              ? {
                  phoneNumber: phoneHint,
                  session: await multiFactor(currentUser).getSession(),
                }
              : {
                  multiFactorHint: phoneHint.hints.find((h) => h.factorId === 'phone'),
                  session: phoneHint.session,
                };

          verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, this.recaptchaVerifier);

          return { verificationId };
        } finally {
          this.resetRecaptcha();
        }
      }),
    );
  }

  verifySMSCode(verificationId: string, verificationCode: string) {
    return this.currentUser().pipe(
      switchMap(async (currentUser) => {
        const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
        await multiFactor(currentUser).enroll(multiFactorAssertion, 'ZEF 2FA');
      }),
    );
  }

  disableMemberMfa(uid: string) {
    return this.cf.post(Commands.MfaAuth, 'mfaAuth/removeMfa', { uid });
  }

  disableSMSMfa() {
    return this.currentUser().pipe(
      switchMap(async (user) => {
        const enrolledFactors = multiFactor(user).enrolledFactors;
        const phoneFactor = enrolledFactors?.find((h) => h.factorId === 'phone');

        if (phoneFactor) {
          await multiFactor(user).unenroll(phoneFactor);
        }
      }),
    );
  }
}
